import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  useCountryClientTypePricingRuleAddMutation,
  useCountryClientTypePricingRuleUpdateMutation,
  useCountryPricingRuleClientTypeListQuery,
} from 'api';
import { Form, FormInstance, FormProps, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { IEditPricing } from './index';
import { getUpdatedPayload } from 'utils/helpers';

export type CountryClientPricingRuleType = {
  countryId: string;
  clientTypeId: string;
  markup: number;
  minMargin: number;
  maxMargin: number;
  clientTypePricingRulesId?: string;
};

export const useClientTypeAddEditPricing = (props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { data: clientTypeData = [] } = useCountryPricingRuleClientTypeListQuery({ countryId: id });

  const [addPricingRules, { isLoading: addIsLoading }] = useCountryClientTypePricingRuleAddMutation();
  const [updatePricingRules, { isLoading: updateIsLoading }] = useCountryClientTypePricingRuleUpdateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [], // Clears the error for filed
        },
      ]);
    }
  }, []);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish: FormProps<CountryClientPricingRuleType>['onFinish'] = useCallback(
    async (values: CountryClientPricingRuleType) => {
      // TODO make backend to accept nul, for clientTypeId
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.countryId = id;
      payload.clientTypePricingRulesId = dataToEdit?.id;

      try {
        const { data, error }: Record<string, any> = await (dataToEdit ? updatePricingRules : addPricingRules)(payload);
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: `Country client type pricing rules was successfully ${dataToEdit ? 'updated' : 'created'}.`,
          });
          onCancel();
        } else if (error?.data?.success === false) {
          Object.values(error?.data?.data || {}).forEach((message) => {
            openNotificationWithIcon({
              type: 'error',
              message: message as string,
            });
          });
        } else if (error?.data?.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      } catch (err) {
        console.error(`Error ${dataToEdit ? 'updating' : 'creating'}  country client type pricing rules:`, err);
        openNotificationWithIcon({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
        });
      }
    },

    [updatePricingRules, openNotificationWithIcon, id, onCancel, dataToEdit, addPricingRules]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
  }, [modalState, form, dataToEdit]);

  return {
    contextHolder,
    form,
    isLoading: addIsLoading || updateIsLoading,
    clearFieldValidation,
    onFinish,
    onCancel,
    clientTypeData: dataToEdit
      ? [
          ...clientTypeData,
          {
            label: dataToEdit.clientType?.name,
            value: dataToEdit.clientType?.id,
          },
        ]
      : clientTypeData,
  };
};
